import { Flex, FlexItem, Text, RetryIcon, carouselNavigationItemSlotClassNames } from "@fluentui/react-northstar";
import { Loader } from "ecom-teams-apps-core";
import { cloneDeep } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import pl from "shared/messages/pl";
import { SearchEntityTypeEnum, V360ReportData } from "view360Card/models";
import FailedToFetchReportError from "../error/FailedToFetchReportError";
import { CompanySegment, Segment } from "../models";
import { DailyReportConfig, DailyReportConfigCompany, DailyReportConfigSegment } from "../models/daily-sales";
import { ReportSection } from "../ReportSection";
import ReportsService from "../service/ReportsService";
import { DailyCompany } from "./DailyCompany";

export interface DailySalesSectionProps {
    entityId: number;
    entityType: SearchEntityTypeEnum;
    isMobileView: boolean;
}

const initialHight = 160;

export const DailySalesSection: React.FC<DailySalesSectionProps> = (props) => {
    const [countDown, setCountDown] = useState<number>();
    const [boxHeight, setBoxHeight] = useState(initialHight);
    const boxRef = useRef<HTMLDivElement | null>(null);
    const [companySegments, setCompanySegments] = useState<CompanySegment[]>([]);

    const queryKeyConfig = ["daily-sales-config"];

    const { isFetching: isConfigFetching, isError: isConfigError, data: configData } = useQuery(
        queryKeyConfig,
        async () => {
            return await ReportsService.getDailyReportConfig();
        }
    );

    const queryKeyReport = ["daily-sales", props.entityId, props.entityType];

    const refetchInterval = () => {
        return configData?.reloadIntervalSeconds ?? 0 > 0 ? (configData?.reloadIntervalSeconds ?? 0) * 1000 : false;
    };

    const { isFetching, isError, data, dataUpdatedAt } = useQuery(
        queryKeyReport,
        async () => {
            return await ReportsService.getDailySales(props.entityId, props.entityType);
        },
        {
            refetchInterval: refetchInterval(),
            enabled: configData !== undefined,
        }
    );

    useEffect(() => {
        if (!isFetching) {
            setBoxHeight(boxRef.current?.clientHeight ?? initialHight);
        }
    }, [isFetching]);

    useEffect(() => {
        if (!configData || !data || isFetching || companySegments.length > 0) {
            return;
        }

        const compSegmentList: CompanySegment[] = data.companyReports.map((c) => {
            const userDefaultCompanySegments =
                configData.companyList.find((companyConfig) => companyConfig.companyCode === c.code)?.segmentList ?? [];

            return {
                companyCode: c.code ?? "",
                companySegmentList: c.segmentList.map((s) => {
                    return new Segment(
                        s.segmentLabel?.code ?? "",
                        s.segmentLabel?.description ?? "",
                        userDefaultCompanySegments.findIndex((i) => i.code === s.segmentLabel?.code) > -1
                    );
                }),
            };
        });
        setCompanySegments(compSegmentList);
    }, [isFetching]);

    useEffect(() => {
        const refreshInterval = configData?.reloadIntervalSeconds ?? 0;
        if (refreshInterval <= 0) {
            return;
        }

        const nextUpdate = new Date(dataUpdatedAt);
        nextUpdate.setSeconds(nextUpdate.getSeconds() + refreshInterval);

        const updateCountdown = () => {
            const timeLeft = nextUpdate.getTime() - new Date().getTime();
            setCountDown(timeLeft);
        };

        updateCountdown();
        const interval = setInterval(() => {
            updateCountdown();
        }, 1000);

        return () => clearInterval(interval);
    }, [dataUpdatedAt]);

    const getTimeLeft = () => {
        let result: number | undefined;
        if (countDown) {
            result = Math.floor(countDown / 1000);
        }
        return result;
    };

    if (isConfigError) {
        return (
            <FailedToFetchReportError message="Wystąpił błąd podczas pobierania konfiguracji raportów sprzedaży dziennej" />
        );
    }

    if (isError) {
        return <FailedToFetchReportError message="Wystąpił błąd podczas pobierania raportów sprzedaży dziennej" />;
    }

    const renderLoader = () => {
        return (
            <Flex fill hAlign="center">
                <Loader text={pl.view360Card.loadingReports} />
            </Flex>
        );
    };

    const onSelectedFilterChanged = async (companyCode: string, segmentList: Segment[]) => {
        setCompanySegments((prev) => {
            const existing = prev.find((i) => i.companyCode === companyCode);
            if (existing) {
                existing.companySegmentList = segmentList;
            }
            return cloneDeep(prev);
        });

        let newDailyReportConfig: DailyReportConfig = {
            reloadIntervalSeconds: configData?.reloadIntervalSeconds ?? 120 ,
            companyList: companySegments.map(c => {
                return {
                    companyCode: c.companyCode,
                    segmentList: c.companySegmentList.filter((segment) => {
                        return segment.selected;
                    }).map((segment, index) => {
                        let newSegment: DailyReportConfigSegment;
                            newSegment = { code: segment.code, index: index};
                            return newSegment;
                    })
                }
            })
        }
        await ReportsService.updateDailyReportConfig(newDailyReportConfig);
    };

    const getContent = () => {
        if (isFetching || isConfigFetching) {
            return renderLoader();
        }

        return (
            <Flex column={props.isMobileView} gap="gap.smaller">
                {data?.companyReports.map((company, idx) => (
                    <FlexItem key={company.code ?? ""} size="size.half">
                        <DailyCompany
                            key={idx.toString()}
                            onSelectedFilterChanged={onSelectedFilterChanged}
                            segmentFilterList={
                                companySegments.find((c) => c.companyCode === company.code)?.companySegmentList ?? []
                            }
                            report={company}
                            isMobileView={props.isMobileView}
                        />
                    </FlexItem>
                ))}
            </Flex>
        );
    };

    const getRefreshInfo = () => {
        const timeLeft = getTimeLeft() ?? -1;
        return (
            <Flex styles={{ height: "20px" }} key="refresh-info" gap="gap.small">
                {timeLeft > 0 && (
                    <>
                        <RetryIcon
                            styles={({ theme: { siteVariables } }) => ({
                                color: siteVariables.colorScheme.default.foreground5,
                            })}
                        />
                        <Text
                            size="small"
                            styles={({ theme: { siteVariables } }) => ({
                                color: siteVariables.colorScheme.default.foreground5,
                            })}
                        >
                            Aktualizacja za {timeLeft} sek.
                        </Text>
                    </>
                )}
            </Flex>
        );
    };

    const getTitleExtensions = () => {
        return [getRefreshInfo()];
    };

    return (
        <Flex fill ref={boxRef} styles={{ height: isFetching || isConfigFetching ? `${boxHeight}px` : undefined }}>
            <ReportSection
                title={"Sprzedaż dzienna"}
                filters={getTitleExtensions()}
                reportContent={getContent()}
                isMobileView={props.isMobileView}
            />
        </Flex>
    );
};
